import { useState, useCallback, useMemo } from 'react';
import { getSessionPartnerships } from '../services/sessionPartnershipService';
import { updatePartnerships } from '../services/partnershipService';
import { shouldHoldRemoteUpcoming } from '../utils/session/offlineQueueConstants';

/**
 * Session partnerships state and load/update logic.
 * @param {Object} params
 * @param {string} params.sessionId
 * @returns {{ partnerships, setPartnerships, partnershipLookup, loadSessionPartnerships, handleUpdatePartnerships }}
 */
export default function useSessionPartnerships({ sessionId, offlineQueue = null, session = null }) {
  const [partnerships, setPartnerships] = useState([]);

  const loadSessionPartnerships = useCallback(async () => {
    if (!sessionId) return;
    if (shouldHoldRemoteUpcoming(offlineQueue, session)) return;
    try {
      console.log('[PARTNERSHIPS] Loading session partnerships for session:', sessionId);
      const result = await getSessionPartnerships(sessionId);
      if (result.success && result.data) {
        const sessionPartnerships = result.data.map(sp => ({
          id: sp.id,
          player1Id: sp.player1Id ?? sp.player1_id,
          player2Id: sp.player2Id ?? sp.player2_id,
          createdAt: sp.createdAt ?? sp.created_at,
        }));
        console.log('[PARTNERSHIPS] Loaded session partnerships:', sessionPartnerships);
        setPartnerships(sessionPartnerships);
      } else {
        console.log('[PARTNERSHIPS] No session partnerships found or error:', result.error);
        setPartnerships([]);
      }
    } catch (error) {
      console.error('[PARTNERSHIPS] Error loading session partnerships:', error);
      setPartnerships([]);
    }
  }, [sessionId, offlineQueue, session]);

  const handleUpdatePartnerships = useCallback((newPartnerships) => {
    setPartnerships(newPartnerships);
    const formalPartnerships = newPartnerships.filter(p => p.club_id && p.season_id);
    if (formalPartnerships.length > 0) {
      updatePartnerships(formalPartnerships);
    }
  }, []);

  const partnershipLookup = useMemo(() => {
    const lookup = {};
    (partnerships || []).forEach(p => {
      const a = p.player1_id ?? p.player1Id;
      const b = p.player2_id ?? p.player2Id;
      if (a != null && b != null) {
        lookup[a] = b;
        lookup[b] = a;
      }
    });
    return lookup;
  }, [partnerships]);

  return {
    partnerships,
    setPartnerships,
    partnershipLookup,
    loadSessionPartnerships,
    handleUpdatePartnerships,
  };
}
